import type { HarmonyPackageBoardColumn } from '@rhythmic/db/types'
import type { KanbanContext, UpdateKanbanColumnPayload } from '@rhythmic/types'
import { KanbanContextSchema, UpdateKanbanColumnSchema } from '@rhythmic/types'
import { useRhythmicLogger } from '#imports'

import { getColumnsCollection } from '../getCollections'

const logger = useRhythmicLogger('useKanbanBoardContext.updateColumn')

function validateParams(payload: UpdateKanbanColumnPayload, context: KanbanContext | undefined) {
  if (!context) {
    logger.error('Attempted to update column without an active kanban context')
    throw new Error('Kanban board context is not ready: missing context for updating a column')
  }

  logger.debug('Validating parameters for handleUpdateColumn', {
    payload,
    context,
  })

  const parsedContext = KanbanContextSchema.parse(context)

  const parsedPayload = UpdateKanbanColumnSchema.parse(payload)

  return {
    context: parsedContext,
    payload: parsedPayload,
  }
}

export default function handleUpdateColumn(payload: UpdateKanbanColumnPayload, context: KanbanContext | undefined) {
  const { context: validatedContext, payload: validatedPayload } = validateParams(payload, context)
  const columnsCollection = getColumnsCollection(validatedContext.boardId)

  const updates: Partial<HarmonyPackageBoardColumn> = {}
  if (validatedPayload.name !== undefined) updates.name = validatedPayload.name
  if (validatedPayload.statusType !== undefined) updates.status_type = validatedPayload.statusType
  if (validatedPayload.position !== undefined) updates.position = validatedPayload.position

  logger.debug('Updating harmony package board column in collection', {
    boardId: validatedContext.boardId,
    columnId: validatedPayload.columnId,
    updates,
  })

  const updateResult = columnsCollection.update(validatedPayload.columnId, (draft: HarmonyPackageBoardColumn) => {
    Object.assign(draft as HarmonyPackageBoardColumn, updates)
  })

  const updatedColumn = Array.isArray(updateResult) ? updateResult[0] : updateResult

  if (!updatedColumn) {
    logger.error('Failed to update kanban column in collection', {
      boardId: validatedContext.boardId,
      columnId: validatedPayload.columnId,
    })
    throw new Error('Failed to update kanban column')
  }

  logger.info('Kanban column updated successfully', {
    boardId: validatedContext.boardId,
    columnId: updatedColumn.id ?? validatedPayload.columnId,
  })

  return updatedColumn
}
